import type { DocFrontendDocState } from '@affine/nbstore';

/** Steps a document passes from its route to a visible editor, in order. */
export const DOCUMENT_LOAD_STEPS = [
  // The workspace doc list is known.
  'doc-list',
  // The doc is in that list.
  'doc-record',
  // Its content is opened.
  'doc-opened',
  // The permission to read it is resolved.
  'access',
  'editor-created',
  // Blocks that lay out the page, e.g. database storage, are ready.
  'layout-ready',
  // The editor rendered and placed its first view.
  'editor-ready',
] as const;

export type DocumentLoadStep = (typeof DOCUMENT_LOAD_STEPS)[number];

/**
 * Plain-text diagnostics for a document that did not finish loading: the step
 * it waits for, when each earlier step was reached and the sync state of its
 * content. Carries no document content.
 */
export function formatDocumentLoadDiagnostics(input: {
  docId: string;
  elapsedMs: number;
  reached: ReadonlyMap<DocumentLoadStep, number>;
  online: boolean;
  docState: DocFrontendDocState | null;
}) {
  const waitingFor = DOCUMENT_LOAD_STEPS.find(step => !input.reached.has(step));
  const state = input.docState;
  return [
    `Document load: ${input.docId}`,
    `Waiting for: ${waitingFor ?? 'mask release'}`,
    `Elapsed: ${Math.round(input.elapsedMs)} ms`,
    `Online: ${input.online ? 'yes' : 'no'}`,
    ...DOCUMENT_LOAD_STEPS.map(step => {
      const at = input.reached.get(step);
      return `Step ${step}: ${at === undefined ? 'not reached' : `${Math.round(at)} ms`}`;
    }),
    state
      ? [
          `Doc state: ready=${state.ready} loaded=${state.loaded}`,
          `updating=${state.updating} syncing=${state.syncing}`,
          `synced=${state.synced} retrying=${state.syncRetrying}`,
        ].join(' ')
      : 'Doc state: unknown',
    ...(state?.syncErrorMessage
      ? [`Sync error: ${state.syncErrorMessage}`]
      : []),
    ...(state?.syncIsolatedFailure
      ? [`Isolated failure: ${JSON.stringify(state.syncIsolatedFailure)}`]
      : []),
  ].join('\n');
}
